import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { TeamReportResponse } from '../response-models/team-report-response.model';
import { TeamMember } from '../response-models/team-member.model';
import { SummaryCounts } from '../response-models/summary-counts.model';
import { MemberOverallStatus } from '../response-models/member-overall-status.model';
import { TestResultStatus } from '../response-models/test-result-status.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';
import { orgPrefix } from '../utils/org-path-utils';

/**
 * Team safety test report: `/organizations/:orgId/team-report` and its CSV / PDF exports
 * (hub `user-team-report-controller.ts`). No entity model, so this is an api-service.
 * Every method emits an `ApiError` on failure.
 *
 * Reference: /agent-os/specs/2026-01-15-team-report-safety-tests/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class TeamReportService extends ApiService {
  constructor() {
    super('team-report');
  }

  /** The organization's team report: members with their test results, the tests and summary counts. */
  getTeamReport(orgId: string): Observable<TeamReportResponse> {
    return this.request('GET', { prefix: orgPrefix(orgId) }, toTeamReport);
  }

  /** The team report as a CSV file. */
  exportCsv(orgId: string): Observable<Blob> {
    return this.download({ prefix: orgPrefix(orgId), path: 'export/csv' });
  }

  /** The team report as a 'summary' or 'detailed' PDF file. */
  exportPdf(orgId: string, type: 'summary' | 'detailed' = 'summary'): Observable<Blob> {
    return this.download({ prefix: orgPrefix(orgId), path: 'export/pdf', query: { type } });
  }

  /**
   * Helper method to trigger file download from Blob
   *
   * @param blob The blob data
   * @param filename The filename for the download
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get status text for display
   *
   * @param status Member or test status
   * @returns Human-readable status text
   */
  getStatusText(status: MemberOverallStatus | TestResultStatus): string {
    switch (status) {
      case 'current':
        return 'Current';
      case 'expired':
        return 'Expired';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'in_progress':
        return 'In Progress';
      case 'not_started':
        return 'Not Started';
      case 'passed':
        return 'Passed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }

  /**
   * Get CSS class for member status badge
   *
   * @param status Member overall status
   * @returns CSS class name
   */
  getMemberStatusClass(status: MemberOverallStatus): string {
    switch (status) {
      case 'current':
        return 'badge-current';
      case 'expired':
        return 'badge-expired';
      case 'in_progress':
        return 'badge-incomplete';
      case 'not_started':
        return 'badge-incomplete';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get CSS class for test status badge
   *
   * @param status Test result status
   * @returns CSS class name
   */
  getTestStatusClass(status: TestResultStatus): string {
    switch (status) {
      case 'passed':
        return 'test-passed';
      case 'failed':
        return 'test-failed';
      case 'expired':
        return 'test-expired';
      case 'expiring_soon':
        return 'test-expiring';
      case 'in_progress':
        return 'test-in-progress';
      case 'not_started':
        return 'test-not-started';
      default:
        return '';
    }
  }

  /**
   * Get icon class for test status
   *
   * @param status Test result status
   * @returns Bootstrap icon class
   */
  getTestStatusIcon(status: TestResultStatus): string {
    switch (status) {
      case 'passed':
        return 'bi-check-circle-fill';
      case 'failed':
        return 'bi-x-circle-fill';
      case 'expired':
        return 'bi-exclamation-circle-fill';
      case 'expiring_soon':
        return 'bi-exclamation-triangle-fill';
      case 'in_progress':
        return 'bi-hourglass-split';
      case 'not_started':
        return 'bi-circle';
      default:
        return 'bi-circle';
    }
  }

  /**
   * Calculate days until expiration
   *
   * @param expiresAt Expiration date string
   * @returns Number of days until expiration (negative if expired)
   */
  getDaysUntilExpiration(expiresAt: string | null): number {
    if (!expiresAt) return 0;
    const expDate = new Date(expiresAt);
    const now = new Date();
    return Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if a date is within the expiring soon threshold
   *
   * @param expiresAt Expiration date string
   * @param threshold Days threshold (default: 30)
   * @returns True if expiring within threshold
   */
  isExpiringSoon(expiresAt: string | null, threshold: number = 30): boolean {
    const days = this.getDaysUntilExpiration(expiresAt);
    return days > 0 && days <= threshold;
  }

  /**
   * Get passed test count for a member
   *
   * @param member Team member
   * @returns Number of passed tests
   */
  getMemberPassedCount(member: TeamMember): number {
    return member.testResults.filter(
      t => t.status === 'passed' || t.status === 'expiring_soon'
    ).length;
  }

  /**
   * Get completion percentage for a member
   *
   * @param member Team member
   * @returns Completion percentage (0-100)
   */
  getCompletionPercentage(member: TeamMember): number {
    const passed = this.getMemberPassedCount(member);
    const total = member.testResults.length;
    return total > 0 ? Math.round((passed / total) * 100) : 0;
  }
}


/** Reads the report; the members' and tests' rows are display-only and taken as sent. */
function toTeamReport(data: unknown): TeamReportResponse {
  const source = isRecord(data) ? data : {};
  const summary = isRecord(source['summary']) ? source['summary'] : {};
  return {
    members: Array.isArray(source['members']) ? (source['members'].filter(isRecord) as unknown as TeamMember[]) : [],
    tests: Array.isArray(source['tests']) ? (source['tests'].filter(isRecord) as unknown as TeamReportResponse['tests']) : [],
    summary: toSummary(summary),
  };
}

function toSummary(source: Record<string, unknown>): SummaryCounts {
  return {
    total: countOrZero(source['total']),
    current: countOrZero(source['current']),
    expiringSoon: countOrZero(source['expiringSoon']),
    expired: countOrZero(source['expired']),
    incomplete: countOrZero(source['incomplete']),
  };
}
